import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  FormHelperText,
} from '@mui/material';
import { Add, Room } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { roomsAPI } from '../../../rooms/services/rooms';
import { useAuth } from '../../../../context/AuthContext';
import { useSnackbar } from '../../../../hooks/mui/useSnackBar';
import LoadErrorAlert from '../LoadErrorAlert';

const CLASSIFICATION_VALUES = ['UNCLASSIFIED', 'SUPERVISED_AREA', 'CONTROLLED_AREA'];

export default function Step3Rooms({ onRefresh }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [form, setForm] = useState({ name: '', area_m2: '', classification: 'UNCLASSIFIED' });
  const [formError, setFormError] = useState({});
  const [saving, setSaving] = useState(false);
  const { showSuccess, showError } = useSnackbar();

  const fetchRooms = useCallback(async () => {
    if (!user?.department?.department_id) return;
    setLoading(true);
    setLoadError(false);
    try {
      const res = await roomsAPI.listRoomsByDepartment(user.department.department_id);
      setRooms(res.data?.rooms || []);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchRooms(); }, [fetchRooms]);

  const handleAddRoom = async () => {
    const errors = {};
    if (!form.name.trim()) errors.name = t('onboarding:step3.errors.name_required');
    const area = parseFloat(form.area_m2);
    if (!form.area_m2 || isNaN(area) || area <= 0) errors.area_m2 = t('onboarding:step3.errors.area_invalid');
    if (Object.keys(errors).length) { setFormError(errors); return; }

    setSaving(true);
    try {
      await roomsAPI.createRoom({
        name: form.name.trim(),
        area_m2: area,
        classification: form.classification,
      });
      setForm({ name: '', area_m2: '', classification: 'UNCLASSIFIED' });
      setFormError({});
      setShowAddForm(false);
      showSuccess(t('onboarding:step3.success.created'));
      await fetchRooms();
      onRefresh();
    } catch {
      showError(t('onboarding:step3.errors.create_failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {t('onboarding:step3.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('onboarding:step3.subtitle')}
      </Typography>

      <Button
        variant="contained"
        startIcon={<Add />}
        onClick={() => setShowAddForm(v => !v)}
        size="small"
        sx={{ mb: 2 }}
      >
        {t('onboarding:step3.add_room')}
      </Button>

      {showAddForm && (
        <Box
          sx={{
            p: 2, mb: 2, border: 1, borderColor: 'divider',
            borderRadius: 1, bgcolor: 'background.paper',
          }}
        >
          <Stack spacing={2}>
            <TextField
              label={t('onboarding:step3.form_name')}
              size="small"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              error={!!formError.name}
              helperText={formError.name}
              autoFocus
              placeholder={t('onboarding:step3.form_name_placeholder')}
            />
            <TextField
              label={t('onboarding:step3.form_area')}
              size="small"
              type="number"
              inputProps={{ min: 0.1, step: 0.1 }}
              value={form.area_m2}
              onChange={e => setForm(f => ({ ...f, area_m2: e.target.value }))}
              error={!!formError.area_m2}
              helperText={formError.area_m2}
            />
            <FormControl size="small" error={!!formError.classification}>
              <InputLabel id="step3-rooms-form-classification-label">{t('onboarding:step3.form_classification')}</InputLabel>
              <Select labelId="step3-rooms-form-classification-label"
                value={form.classification}
                label={t('onboarding:step3.form_classification')}
                onChange={e => setForm(f => ({ ...f, classification: e.target.value }))}
              >
                {CLASSIFICATION_VALUES.map(val => (
                  <MenuItem key={val} value={val}>
                    {t(`onboarding:step3.classifications.${val}`)}
                  </MenuItem>
                ))}
              </Select>
              {formError.classification && (
                <FormHelperText>{formError.classification}</FormHelperText>
              )}
            </FormControl>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" size="small" onClick={handleAddRoom} disabled={saving}>
                {saving ? <CircularProgress size={16} /> : t('common:buttons.save')}
              </Button>
              <Button size="small" onClick={() => { setShowAddForm(false); setFormError({}); }}>
                {t('common:buttons.cancel')}
              </Button>
            </Stack>
          </Stack>
        </Box>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : loadError ? (
        <LoadErrorAlert onRetry={fetchRooms} />
      ) : rooms.length === 0 ? (
        <Alert severity="info">
          {t('onboarding:step3.no_rooms')}
        </Alert>
      ) : (
        <Stack spacing={1.5} mt={1}>
          {rooms.map(room => (
            <Card key={room.room_id} variant="outlined">
              <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Room color="action" fontSize="small" />
                  <Typography fontWeight={600}>{room.name}</Typography>
                  <Chip label={`${room.area_m2} m²`} size="small" variant="outlined" />
                  <Chip
                    label={t(`onboarding:step3.classifications.${room.classification}`)}
                    size="small"
                    variant="outlined"
                    color="info"
                  />
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
}
